/**
 * Who may change a password, and what has to be proved first.
 *
 * Pulled out of api.ts as plain functions so the rules can be tested without
 * a Supabase client, a session or a network. The decision is the part worth
 * getting right; signing in to check a password is just the errand it sends.
 *
 * The screen has always shown a "Current password" box and always refused to
 * continue without it. It was never checked -- updateProfile accepted the
 * value and called auth.updateUser straight past it, so any text at all let a
 * password through. That is the hole these rules close.
 */

/** The shape of one Supabase identity; only the provider matters here. */
export interface Identity {
  provider: string
}

/**
 * Whether this account can sign in with an email and password.
 *
 * Read from the identity list rather than assumed. api.me() used to report
 * `hasPassword: true` for everybody, which put a "Current password" box in
 * front of people who sign in with Google and have no password to type. While
 * the box was decorative that was merely confusing; the moment it is enforced
 * it would stop them setting a first password at all.
 *
 * A Google account that was given a password through the old, unchecked
 * screen may hold the password without carrying an email identity -- GoTrue
 * adds the identity on sign-up, not on a later password write. Such an
 * account is treated as having none, so it can still set one. That is the
 * safe direction to be wrong in: it leaves those few exactly where they are
 * today rather than locking them out.
 */
export function hasEmailPassword(identities: readonly Identity[] | null | undefined): boolean {
  return (identities ?? []).some((i) => i.provider === 'email')
}

/**
 * What to do about a password change.
 *
 * `allow`        no password on the account -- this is a first one, and there
 *                is nothing to prove.
 * `need-current` the account has a password and none was offered. Refused
 *                before anything is written.
 * `verify`       check this against the account, then proceed.
 */
export type PasswordGate =
  | { kind: 'allow' }
  | { kind: 'need-current' }
  | { kind: 'verify'; password: string }

export function passwordGate(
  accountHasPassword: boolean,
  currentPassword: string | undefined | null,
): PasswordGate {
  if (!accountHasPassword) return { kind: 'allow' }

  // Trimmed only to test for emptiness. The password itself is never trimmed
  // before it is checked -- leading and trailing spaces are part of it.
  const given = (currentPassword ?? '').trim().length > 0
  if (!given) return { kind: 'need-current' }

  return { kind: 'verify', password: currentPassword as string }
}

/** Just enough of a Supabase AuthError to tell the two failures apart. */
export interface SignInFailure {
  status?: number
  code?: string
  message?: string
}

/**
 * Why a verification sign-in failed.
 *
 * `wrong`   the password is not the account's. Say so.
 * `problem` something else -- rate limiting, an outage, a network fault. The
 *           password may well be right, so this must NOT be reported as a bad
 *           password, and the change must not go ahead either. Refusing on an
 *           unknown answer is the only safe reading of "I could not check".
 */
export function classifySignInError(
  error: SignInFailure | null | undefined,
): 'ok' | 'wrong' | 'problem' {
  if (!error) return 'ok'

  if (error.code === 'invalid_credentials' || error.code === 'invalid_grant') return 'wrong'

  // 400 is what GoTrue returns for a bad email/password pair. 429 is rate
  // limiting and 5xx is an outage -- neither says anything about the password.
  if (error.status === 400) return 'wrong'

  return 'problem'
}
